import React, { createContext, useContext, useState } from 'react';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import { FCWithChildren } from 'common/types';

type BankAccountsReturn = ReturnType<typeof useCompanyBankAccounts>;
type BankAccounts = BankAccountsReturn['bankAccounts'];

export type CashflowBankAccountsContextValue = {
  loading: boolean;
  bankAccounts: BankAccounts | undefined;
  mainBankAccountId: string | null | undefined;
  selectedBankAccountId: string | null;
  setSelectedBankAccountId: (id: string) => void;
};

const CashflowBankAccountsContext =
  createContext<CashflowBankAccountsContextValue | null>(null);

// This could potentially become app-wide
export const CashflowBankAccountsContextProvider: FCWithChildren = ({
  children,
}) => {
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<
    string | null
  >(null);

  const { bankAccounts, loading: loadingBankAccounts } =
    useCompanyBankAccounts();
  const { loading: loadingBankAccountId, mainBankAccountId } =
    useMainBankAccountId({
      onCompleted: (data) => {
        setSelectedBankAccountId(data?.viewer?.bankAccountsIds?.[0]);
      },
    });

  const loading = loadingBankAccounts || loadingBankAccountId;

  return (
    <CashflowBankAccountsContext.Provider
      value={{
        bankAccounts,
        loading,
        mainBankAccountId,
        selectedBankAccountId,
        setSelectedBankAccountId,
      }}
    >
      {children}
    </CashflowBankAccountsContext.Provider>
  );
};

export const useCashflowBankAccountsContext = () => {
  const ctx = useContext(CashflowBankAccountsContext);

  if (!ctx) {
    throw new Error(
      'Did you forget to wrap with CashflowBankAccountsContextProvider?',
    );
  }

  return ctx;
};
